<template>
  <div class="oct-favorite-form">
    <div class="oct-grid">
      <div class="oct-grid__row">
        <div class="oct-grid__col oct-grid__col--12">
          <oct-article-header
            title="SAVES & FOLLOWS"
          />

          <div class="oct-favorite-form-tab">
            <ul class="oct-favorite-form-tab-list">
              <li
                v-for="(item, i) in ['artists', 'artworks', 'galleries', 'exhibitions']"
                :key="i"
                class="oct-favorite-form-tab-list__item"
                :class="{
                  'oct-favorite-form-tab-list__item--active': type === item
                }"
                @click="type = item;getItems()"
              >
                <div class="oct-favorite-form-tab-list__content">
                  <span class="oct-favorite-form-tab-list__primary-text">{{ item.replace('galleries', 'galleries & museums').toUpperCase() }}</span>
                  <span class="oct-favorite-form-tab-list__primary-text">
                    {{ getCounts(item) }}
                  </span>
                </div>
              </li>
            </ul>
          </div>

          <oct-card-list
            class="oct-favorite-form__items"
            v-if="items.length && !state.processing"
          >
            <oct-card-list-item
              v-for="(item, i) in items"
              :key="i"
              :cols="4"
            >
              <oct-card
                v-bind="item"
                @action="toggleFavorite(item)"
              />
            </oct-card-list-item>
          </oct-card-list>

          <oct-article-header
            title="NO ITEMS FOUND."
            v-else-if="!state.processing"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OctApiAdaptor from '@/components/api-adaptor/OctApiAdaptor.js'
import OctPrototypeForm from '@/components/prototype-form/OctPrototypeForm.js'
import OctCardList from '@/components/card/OctCardList.vue';
import OctCardListItem from '@/components/card/OctCardListItem.vue';
import OctCard from '@/components/card/OctCard.vue';
import OctArticleHeader from '@/components/article-header/OctArticleHeader.vue';

export default {
  mixins: [OctPrototypeForm],
  name: 'OctFavoriteForm',
  components: {
    OctCardList,
    OctCardListItem,
    OctCard,
    OctArticleHeader
  },
  props: {
    /** Current user  */
    user: {
      type: Object,
      default: () => {}
    },
    /** Access token for Auth0 */
    token: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      /** Current items */
      items: [],
      type: 'artists'
    }
  },
  methods: {
    /** Toggle favorite */
    async toggleFavorite (item) {
      // The type
      const type = this.type.replace('galleries', 'gallerys').replace(/s$/, '')

      if (item.done)
        await OctApiAdaptor.removeFavorite(item.id, this.token)
      else
        await OctApiAdaptor.addFavorite(item.id, type, this.token)

      // Toggle done
      item.done = !item.done

      /** @event update */
      this.$emit('update')
    },
    /** Wait for all images to finish loading */
    async loadcomplete () {
      // Images
      const images = Array.from(this.$el.querySelectorAll('.oct-card__thumbnail img'))
      // Promises
      return Promise.all(images.map(item => {
        if (!item.complete)
          return new Promise(resolve => {
            item.addEventListener('load', resolve)
            item.addEventListener('error', resolve)
          })
        else
          return Promise.resolve()
      }))
    },
    async getItems () {
      // Processing
      this.state.processing = true

      let items = []

      try {
        if (this.type === 'artworks') {
          items = (await OctApiAdaptor.getArtworks({
            ids: this.user.favoriteWorks.join(','),
            counts: 1000
          }, this.token)).items

          items.forEach(item => {
            const artist = item.artists.find(i => i.data.status === 'public')
            // Texts
            item.headline = this.getLocaleString(item, 'title')
            item.caption = item.publishedAt + '/' + item.materials.join(', ')
            item.title = ''
            // Media
            item.media = item.images.map(i => {return {image: i}}).slice(0, 1)

            // Author
            if (artist)
              item.author = {
                media: {
                  image: artist.profileIcon
                },
                text: this.getLocaleString(artist, 'tradeName'),
                href: `/artists/${artist.displayName}`
              }
            
            // Href
            item.href = `/artworks/${item.id}`
          })
        } else if (this.type === 'artists') {
          const artists = (await OctApiAdaptor.getUsers({
            ids: this.user.favoriteArtists.join(','),
            counts: 1000
          }, this.token)).items

          artists.forEach(item => {
            item.media = []
            // Media
            item.artworks.forEach(i => {
              i.images.length && item.media.push({image: i.images[0]})
            })
            // Narrow down images
            item.media = item.media.slice(0, 2)

            // Author
            item.author = {
              media: {
                image: item.profileIcon
              },
              text: this.getLocaleString(item, 'tradeName'),
              href: `/artists/${item.displayName}`
            }
            // Texts
            item.caption = [item.position.join(', '), item.base].filter(i => i !== '').join(' / ') +
                            `\n${item.numberOfWorks} works`
            // Href
            item.href = `/artists/${item.displayName}`
            // Layout
            item.reverse = true

            items.push(item)
          })
        } else if (this.type === 'galleries') {
          items = (await OctApiAdaptor.getUsers({
            ids: this.user.favoriteGalleries.join(','),
            counts: 1000
          }, this.token)).items

          items.forEach(item => {
            // Media
            item.media = {image: item.coverImage}
            // Author
            item.author = {
              media: {
                image: item.profileIcon
              },
              text: this.getLocaleString(item, 'tradeName'),
              href: `/spaces/${item.displayName}`
            }
            // Texts
            item.caption = [item.base, (item.birthday ? 'est. ' : '') + item.birthday].filter(i => i != '').join(' / ')
            // Href
            item.href = `/spaces/${item.displayName}`
            // Layout
            item.square = true
            item.reverse = true
          })
        } else if (this.type === 'exhibitions') {
          items = (await OctApiAdaptor.getExhibitions({
            ids: this.user.favoriteExhibitions.join(','),
            counts: 1000
          }, this.token)).items

          items.forEach(item => {
            // Media
            if (item.images.length)
              item.media = [{image:item.images[0]}]

            // Texts
            item.title = `Exhibition: ${item.type}`
            item.headline = this.getLocaleString(item, 'title')
            item.subtitle = this.periodToString(new Date(item.startDate), new Date(item.endDate))
            item.text = this.getLocaleString(item['gallery'], 'tradeName')
            // Href
            item.href = `/exhibitions/${item.id}`
          })
        }
      } catch (thrown) {
        alert('お気に入りを取得できませんでした')
        console.log(thrown)
      }

      // Normalize items
      items.forEach(item => {
        // Use small images
        if (Array.isArray(item.media))
          item.media.forEach(i => i.image.standardResolution = i.image.lowResolution)
        else if (item.media)
          item.media.image.standardResolution = item.media.image.lowResolution

        // Layout
        item.dense = true
        item.done = true

        // Enable action
        item.action = true
      })

      this.$set(this, 'items', items)

      // Processing
      this.state.processing = false

      await this.$nextTick()
      await this.loadcomplete()
    },
    getCounts (type) {
      switch (type) {
        case 'artists':
          return this.user.favoriteArtists.length
        case 'galleries':
          return this.user.favoriteGalleries.length
        case 'artworks':
          return this.user.favoriteWorks.length
        case 'exhibitions':
          return this.user.favoriteExhibitions.length
      }
    },
    getTitle (item) {
      if (this.type === 'artwork') {
        return ''
      } else {
        return `Exhibition: ${item.type}`
      }
    },
    getHeadline (item) {
      if (this.type === 'exhibition') {
        return this.getLocaleString(item, 'title') + '\n' +
                this.dateToString(new Date(item.startDate))
      } else {
        return this.getLocaleString(item, 'title')
      }
    },
    getText (item) {
      if (this.type === 'artworks') {
        return `${item.publishedAt} / ${item.materials.join(', ')}`
      } else if (this.type === 'exhibitions') {
        return this.getLocaleString(item['gallery'], 'tradeName')
      } else if (this.type === 'news') {
        return this.dateToString(new Date(item.publishedAt))
      }
    },
    getHref (item) {
      if (this.type === 'artists' || this.type === 'galleries') {
        return `/${this.type === 'galleries' ? 'spaces' : this.type}/${item.displayName}`
      } else {
        return `/${this.type}/${item.id}`
      }
    }
  },
  mounted () {
    this.getItems()
  }
}
</script>

<style scoped lang="scss">
@import "../theme/variables";
@import "../grid/variables";
@import "../typography/variables";
@import "../typography/mixins";

.oct-favorite-form {
  margin-top: oct-rem(72);
  overflow: hidden;
}

.oct-favorite-form-tab {
  margin: 0 oct-rem(-20) oct-rem(94);
  padding: 0 oct-rem(20);
  overflow: hidden;
  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  margin: 0 0 oct-rem(94);
  padding: 0;
}
}

.oct-favorite-form-tab-list {
  margin: 0 oct-rem(-6);
  padding: 0;
  display: flex;
  list-style: none;

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  margin: 0 oct-rem(-8);
  padding: 0;
}
}

.oct-favorite-form-tab-list__item {
  flex: 0 0 oct-rem(148);
  max-width: oct-rem(148);
  padding: 0 oct-rem(6);

  &:last-child {
    flex: 0 0 oct-rem(148+14);
    max-width: oct-rem(148+14);
    padding-right: oct-rem(20);
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  &, &:last-child {
    flex: 0 0 25%;
    max-width: 25%;
    padding: 0 oct-rem(12);
  }
}
}

.oct-favorite-form-tab-list__content {
  display: flex;
  justify-content: space-between;
  padding: oct-rem(8-1) 0 0;
  border-top: oct-rem(1) solid rgba(0,0,0,.3);
  cursor: pointer;

  .oct-favorite-form-tab-list__item--active &, &:hover {
    border-top: oct-rem(2) solid $oct-theme--primary;
    padding-top: oct-rem(8-2);
  }
}

.oct-favorite-form-tab-list__primary-text {
  font-size: oct-rem(11);
  line-height: (15/11);
  margin: (-3em/11) 0;
  display: block;

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  font-size: oct-rem(13);
  line-height: (18/13);
  margin: (-4em/13) 0;
}
}
</style>
